/**
 * useCompetitionAdmin — the writes that define a competition and move it
 * through its lifecycle.
 *
 * Competition, group and session documents (create / update / delete), then
 * activate, close, the attempt locks, the climbs mirror, and the challenge
 * variant's participation and close paths. The organiser's day-of controls are
 * next door in `useCompetitionSessions`; who is entered is in
 * `useCompetitionRegistry`.
 */
import { useCallback } from "react";
import {
  collection, doc, setDoc, updateDoc, getDocs, getDoc,
  writeBatch, serverTimestamp, query, where, deleteField, FieldPath,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useConfirm } from "../../components/ui";
import { buildCompetitionDraft, buildGroupDoc, buildSessionDoc } from "@sendly/shared";
import { buildCloseSnapshot } from "../../competition/closeSnapshot";
import { fetchCompParts } from "../../utils/competitionFetch";
import { writeChallengeParticipation } from "../../utils/challengeData";
import { callableRef } from "../../utils/callFunction";

const deleteCompCF = callableRef("deleteCompetitionCascade");

/** The values of a challenge's `excludedUsers` map (docs/spec/09-challenge.md). */
const CHALLENGE_EXCLUSION_MODES = ["hidden", "no_rank"];
const closeChallengeCF = callableRef("closeChallengeNow");
const syncClimbsCF = callableRef("syncCompetitionClimbs");

export function useCompetitionAdmin({ gymId, gymMeta, authUser, competitions, withActor, deleteAttributed, writeSession }) {
  const confirm = useConfirm();

  // ── Competition CRUD ──

  const createCompetition = useCallback(async (data) => {
    if (!gymId || !authUser) return null;
    const compId = "comp_" + Date.now();
    // The field set and its defaults are the shared builder's — the MCP server
    // creates drafts with the same one. The app creates groups in the editor,
    // so the builder's `groups` (none without `groupNames`) are not written here.
    // `country` decides the default language the three organiser texts mirror
    // (gymDefaultLang, packages/shared/src/localizedText.js).
    const { doc: draft } = buildCompetitionDraft(data, { gymId, country: gymMeta?.country });
    const compDoc = {
      ...draft,
      createdBy: authUser.uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    await setDoc(doc(db, "gyms", gymId, "competitions", compId), withActor(compDoc));
    return compId;
  }, [gymId, gymMeta, authUser, withActor]);

  // `followUp`: a write that completes this client's own save a moment ago —
  // the server's updatedAt is then ours, and the lock below would mistake it
  // for another user's edit.
  const updateCompetition = useCallback(async (compId, data, { followUp = false } = {}) => {
    if (!gymId || !compId) return;
    const compRef = doc(db, "gyms", gymId, "competitions", compId);

    // Optimistic lock: check if competition was modified since we last read it
    const localComp = competitions.find(c => c.id === compId);
    if (!followUp && localComp?.updatedAt) {
      const currentSnap = await getDoc(compRef);
      const serverUpdatedAt = currentSnap.data()?.updatedAt;
      if (serverUpdatedAt && localComp.updatedAt.toMillis && serverUpdatedAt.toMillis) {
        if (serverUpdatedAt.toMillis() > localComp.updatedAt.toMillis()) {
          const proceed = await confirm({
            body: "This competition was modified by another user. Overwrite their changes?",
            danger: true,
          });
          if (!proceed) return;
        }
      }
    }

    await updateDoc(compRef, withActor({ ...data, updatedAt: serverTimestamp() }));
  }, [gymId, competitions, confirm]);

  /**
   * Server-side cascade. This used to enumerate the subcollections here and
   * delete each one with a single `writeBatch`, which Firestore caps at 500
   * operations — so a 500-entrant competition failed on `registrations` and a
   * real one failed on `attempts` (one doc per competitor × route, thousands),
   * leaving the comp half-deleted. The CF sweeps every descendant with
   * `recursiveDelete` and takes the parent doc out first so the audit triggers
   * know to skip. See functions/src/cascadeDelete.js.
   */
  const deleteCompetition = useCallback(async (compId) => {
    if (!gymId || !compId) return;
    await deleteCompCF({ gymId, compId });
  }, [gymId]);

  // ── Group CRUD ──

  const saveGroup = useCallback(async (compId, groupId, data) => {
    if (!gymId || !compId) return;
    const id = groupId || doc(collection(db, "gyms", gymId, "competitions", compId, "groups")).id;
    const path = doc(db, "gyms", gymId, "competitions", compId, "groups", id);
    // The field set is the shared `buildGroupDoc` — the MCP draft tools write
    // the same shape; only `createdAt` and the actor stamp are ours.
    await setDoc(path, withActor({
      ...buildGroupDoc(data, 0),
      createdAt: data.createdAt || serverTimestamp(),
    }), { merge: true });
    return id;
  }, [gymId, withActor]);

  // Facts about the groups/sessions, denormalized onto the competition doc:
  // the {groupId/sessionId -> name} lookup maps, and whether any group is
  // judged. groupName/sessionName are no longer stored on each registration doc
  // (they went stale on rename); instead the editor refreshes all of this on
  // every save so it is always current and available for the competition
  // list cards / friends box WITHOUT loading the groups/sessions
  // subcollections for every comp. Plain last-write-wins merge (derived
  // data, no optimistic lock needed) — mirrors the close-time snapshot.
  const setCompetitionDenorm = useCallback(async (compId, { groupNames, sessionNames, hasJudgedGroups }) => {
    if (!gymId || !compId) return;
    await setDoc(
      doc(db, "gyms", gymId, "competitions", compId),
      withActor({
        groupNames: groupNames || {},
        sessionNames: sessionNames || {},
        // Same reason as the name maps: the competition LIST cannot load every
        // comp's groups, but it must know whether a competitor may bring a
        // guest — which is only true where a judged group exists.
        hasJudgedGroups: !!hasJudgedGroups,
      }),
      { merge: true }
    );
  }, [gymId, withActor]);

  const deleteGroup = useCallback(async (compId, groupId) => {
    if (!gymId || !compId || !groupId) return;
    // Cascade: unassign registrations from this group. groupName used to
    // be cleared here too — it's no longer denormalized on the reg doc, so
    // only groupId needs nulling.
    const regsCol = collection(db, "gyms", gymId, "competitions", compId, "registrations");
    const assignedRegs = await getDocs(query(regsCol, where("groupId", "==", groupId)));
    if (assignedRegs.size > 0) {
      // One shared bulk id: the audit trigger logs a single "N entrants
      // unassigned" entry instead of one per registration.
      const bulkOp = { id: `groupDelete_${groupId}_${Date.now()}`, count: assignedRegs.size };
      const batch = writeBatch(db);
      assignedRegs.docs.forEach(d => batch.update(d.ref, withActor({ groupId: null }, bulkOp)));
      await batch.commit();
    }
    await deleteAttributed(doc(db, "gyms", gymId, "competitions", compId, "groups", groupId));
  }, [gymId, withActor, deleteAttributed]);

  // ── Session CRUD ──

  const saveSession = useCallback(async (compId, sessionId, data) => {
    if (!gymId || !compId) return;
    const id = sessionId || doc(collection(db, "gyms", gymId, "competitions", compId, "sessions")).id;
    const path = doc(db, "gyms", gymId, "competitions", compId, "sessions", id);
    // Base fields from the shared `buildSessionDoc` (the MCP draft tools write
    // the same shape); status / timing / finals below stay this hook's job.
    const writeData = {
      ...buildSessionDoc(data, 0),
      createdAt: data.createdAt || serverTimestamp(),
    };
    // Only write status/timing if explicitly provided — preserves running/finished state on edit
    if (data.status !== undefined) writeData.status = data.status;
    else if (!sessionId) writeData.status = "scheduled";
    if (data.actualStartTime !== undefined) writeData.actualStartTime = data.actualStartTime;
    if (data.actualEndTime !== undefined) writeData.actualEndTime = data.actualEndTime;
    if (data.remainingSeconds !== undefined) writeData.remainingSeconds = data.remainingSeconds;
    if (data.pausedAt !== undefined) writeData.pausedAt = data.pausedAt;
    // Finals session fields
    if (data.type !== undefined) writeData.type = data.type;
    if (data.finals !== undefined) writeData.finals = data.finals;
    await setDoc(path, withActor(writeData), { merge: true });
    return id;
  }, [gymId, withActor]);

  const deleteSession = useCallback(async (compId, sessionId) => {
    if (!gymId || !compId || !sessionId) return;
    // Cascade: unassign registrations from this session. sessionName is no
    // longer denormalized on the reg doc, so only sessionId needs nulling.
    const regsCol = collection(db, "gyms", gymId, "competitions", compId, "registrations");
    const assignedRegs = await getDocs(query(regsCol, where("sessionId", "==", sessionId)));
    if (assignedRegs.size > 0) {
      // Shared bulk id — see deleteGroup.
      const bulkOp = { id: `sessionDelete_${sessionId}_${Date.now()}`, count: assignedRegs.size };
      const batch = writeBatch(db);
      assignedRegs.docs.forEach(d => batch.update(d.ref, withActor({ sessionId: null }, bulkOp)));
      await batch.commit();
    }
    await deleteAttributed(doc(db, "gyms", gymId, "competitions", compId, "sessions", sessionId));
  }, [gymId, withActor, deleteAttributed]);

  // ── Competition lifecycle ──

  const activateCompetition = useCallback(async (compId) => {
    if (!gymId) return;
    // Warning is handled in AdminCompetitions UI — no blocking here
    await updateDoc(doc(db, "gyms", gymId, "competitions", compId), withActor({
      status: "active",
      updatedAt: serverTimestamp(),
    }));
    return { success: true };
  }, [gymId, competitions, withActor]);

  // Competition-wide attempt-recording lock. When true, judges/competitors
  // can't record new attempts (gymAdmin/organizer explicit corrections still
  // pass — see saveAttempt). Per-session lock below freezes one session only.
  const setCompetitionAttemptsLocked = useCallback(async (compId, locked) => {
    if (!gymId) return;
    await updateDoc(doc(db, "gyms", gymId, "competitions", compId), withActor({
      attemptsLocked: !!locked,
      updatedAt: serverTimestamp(),
    }));
  }, [gymId, withActor]);

  // `writeSession` was an undeclared dependency while it was a closure
  // sibling — harmless, because its own identity tracks `gymId` and nothing
  // else. Now that it arrives as a parameter, an omission here is a stale
  // closure waiting for the day the sessions hook gains a dependency, so it
  // is declared.
  const setSessionAttemptsLocked = useCallback(async (compId, sessionId, locked) => {
    if (!gymId) return;
    await writeSession(compId, sessionId, {
      attemptsLocked: !!locked,
    });
  }, [gymId, writeSession]);

  const closeCompetition = useCallback(async (compId) => {
    if (!gymId || !authUser) return;
    const basePath = `gyms/${gymId}/competitions/${compId}`;
    const comp = competitions.find(c => c.id === compId);
    if (!comp) return;

    // Freeze competition to block new attempt writes (Firestore rules check for "closing" status)
    await updateDoc(doc(db, basePath), withActor({ status: "closing", updatedAt: serverTimestamp() }));
    // Brief wait to let in-flight writes land
    await new Promise(resolve => setTimeout(resolve, 2000));

    try {
      // Load groups, sessions, registrations, attempts directly from Firestore
      // so we don't depend on activeCompId being set
      const {
        groups: compGroups, sessions: compSessions,
        registrations: compRegs, attempts: compAttempts,
      } = await fetchCompParts(gymId, compId, ["groups", "sessions", "registrations", "attempts"]);

      // The frozen rankings (groups + finals) and the export's group names —
      // the pure snapshot the E2E seed's closed competition is built with too.
      const { results, groupNames } = buildCloseSnapshot({
        settings: comp.settings,
        groups: compGroups,
        sessions: compSessions,
        registrations: compRegs,
        attempts: compAttempts,
      });
      // Write results to subcollection instead of inline on competition doc
      for (const [groupId, ranking] of Object.entries(results)) {
        await setDoc(doc(db, basePath, "results", groupId), {
          rankings: ranking,
          updatedAt: serverTimestamp(),
        });
      }

      await updateDoc(doc(db, basePath), withActor({
        status: "closed",
        groupNames,
        closedAt: serverTimestamp(),
        closedBy: authUser.uid,
        updatedAt: serverTimestamp(),
      }));

      // Mirror every top into the climbers' ordinary statistics — an `ascents`
      // doc plus a daily-log entry, marked with this competition's id.
      //
      // AFTER the close, and deliberately NOT inside the try/catch rollback
      // above: the competition is legitimately closed at this point, and a
      // statistics sync that fails must not undo that. The operation is
      // idempotent (deterministic ascent / log-entry ids), so it can simply be
      // run again from the competition row.
      try {
        await syncClimbsCF({ gymId, compId });
      } catch (syncErr) {
        console.error("syncCompetitionClimbs after close failed:", syncErr);
      }
    } catch (err) {
      // Rollback to active if close failed
      await updateDoc(doc(db, basePath), withActor({ status: "active", updatedAt: serverTimestamp() })).catch(() => {});
      throw err;
    }
  }, [gymId, authUser, competitions, withActor]);

  /**
   * Re-run the competition → climbs mirror for a closed competition.
   *
   * The close flow fires this automatically, but that call is deliberately
   * fault-tolerant: a competition stays closed even if the statistics sync
   * fails. This is the way back in. Safe to run any number of times — ascent
   * and log-entry ids are deterministic, so a second run writes nothing.
   */
  const syncCompetitionClimbs = useCallback(async (compId) => {
    if (!gymId || !compId) return null;
    const res = await syncClimbsCF({ gymId, compId });
    return res?.data || null;
  }, [gymId]);

  // ── Challenge (passive leaderboard challenge) ──

  // Climber self-toggles their own participation control doc — the one write
  // the public board uses too (`writeChallengeParticipation`).
  const setChallengeParticipation = useCallback(
    (compId, patch = {}) => writeChallengeParticipation({ gymId, compId, uid: authUser?.uid, patch }),
    [gymId, authUser?.uid]
  );

  // One climber's challenge exclusion — written as that ONE key of the
  // `excludedUsers` map (a field path), never as the whole map. The editor's
  // Participants tab used to rebuild the map from the `competition` prop and
  // write it back; on the competition tab that prop is the snapshot taken when
  // the editor opened, so a second exclusion rewrote the map without the first
  // (hide one climber, then take another out of the ranking → the first one
  // back on the board).
  // A per-key write cannot lose another key, whoever wrote it and whenever.
  // `mode`: "hidden" | "no_rank"; anything else removes the key (counts again).
  const setChallengeExclusion = useCallback(async (compId, uid, mode) => {
    // A missing id is a caller bug: throw, so the tab puts its select back
    // instead of showing a choice that was never written.
    if (!gymId || !compId || !uid) throw new Error("setChallengeExclusion: gymId, compId and uid are required");
    const value = CHALLENGE_EXCLUSION_MODES.includes(mode) ? mode : deleteField();
    const stamp = withActor({ updatedAt: serverTimestamp() });
    await updateDoc(
      doc(db, "gyms", gymId, "competitions", compId),
      new FieldPath("excludedUsers", uid), value,
      ...Object.entries(stamp).flat(),
    );
  }, [gymId, withActor]);

  // A challenge's route exclusions: the changed keys of `excludedRoutes` in one
  // update (one recompute), never the whole map, for the same reason as above.
  // `changes`: { [routeId]: displayName } to exclude, { [routeId]: null } to let back.
  const setChallengeRouteExclusions = useCallback(async (compId, changes) => {
    if (!gymId || !compId) throw new Error("setChallengeRouteExclusions: gymId and compId are required");
    const pairs = Object.entries(changes || {}).flatMap(([routeId, name]) => [
      new FieldPath("excludedRoutes", routeId), name ? { name } : deleteField(),
    ]);
    if (pairs.length === 0) return;
    const stamp = withActor({ updatedAt: serverTimestamp() });
    await updateDoc(
      doc(db, "gyms", gymId, "competitions", compId),
      ...pairs,
      ...Object.entries(stamp).flat(),
    );
  }, [gymId, withActor]);

  // Manual "close now" for a challenge — the `closeChallengeNow` callable runs
  // the same `finalizeOne` as the daily auto-close (results/overall + status
  // "closed", closedBy = the admin). The client no longer scores: the server is
  // the one scorer for both sources, so a hand-closed board equals the live one.
  const closeChallenge = useCallback(async (compId) => {
    if (!gymId || !authUser || !compId) return;
    try {
      await closeChallengeCF({ gymId, compId });
    } catch (err) {
      console.error("[useCompetition] closeChallenge failed:", err);
      throw err;
    }
  }, [gymId, authUser]);

  return {
    createCompetition, updateCompetition, deleteCompetition,
    saveGroup, deleteGroup, setCompetitionDenorm,
    saveSession, deleteSession,
    activateCompetition, closeCompetition, syncCompetitionClimbs,
    setCompetitionAttemptsLocked, setSessionAttemptsLocked,
    setChallengeParticipation, setChallengeExclusion, setChallengeRouteExclusions, closeChallenge,
  };
}
